import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
    useCookieLocation,
    useAllHEMTLocationsWithWorktypes,
    useLocationsByLoggedInUserRole,
    useWorktypes,
} from '../hooks';
import Container from '../components/Layout/Container';
import MultiSelectWorktypeLocDateRangeFilter from '../components/General/MultiSelectWorktypeLocDateRangeFilter';
import UserContext from '../store/User/UserContext';
import EventReductionTable from '../components/Events/EventReductionTable';
import { useDeleteReduction, useReductions, usePostReduction } from '../hooks/api/reductions';
import EventReductionDialog from '../components/Events/EventReductionDialog';
import { getReductionById } from '../services/reduction';
import EventReductionExportButton from '../components/Events/EventReductionExportButton';
import GridContainer from '../components/Layout/GridContainer';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import dayjs from '@utils/dayjs';

const Reduction = () => {
    const { t } = useTranslation('allDeltas');
    const loggedInUser = useContext(UserContext);

    const [locationOptions] = useLocationsByLoggedInUserRole({
        prependAllLocationsOption: loggedInUser?.isTEFUser,
    });
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedLocations, setSelectedLocations] = useExpireLocalStorage('reduction-selected-locations', []);
    const [locationOptionsForDialog] = useAllHEMTLocationsWithWorktypes();
    const [worktypeOptions] = useWorktypes({
        prependAllWorktypesOption: true,
        location: loggedInUser?.isOSPUser ? selectedLocation : undefined,
    });
    const [selectedWorktypes, setSelectedWorktypes] = useExpireLocalStorage(
        'reduction-selected-worktypes',
        worktypeOptions.length > 0 ? [worktypeOptions[0]] : []
    );
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs().add(6, 'months'));

    const [selectedRow, setSelectedRow] = useState(null);

    const [showEventReductionDialog, setShowEventReductionDialog] = useState(false);
    const filterParams = {
        startDate,
        endDate,
        location: loggedInUser?.isOSPUser ? selectedLocation : selectedLocations.map((loc) => loc.id),
        worktype: selectedWorktypes.map((worktype) => worktype.id),
    };
    const { reductions, durationTotal, reductionTotal } = useReductions(filterParams);

    const { mutateAsync: handleDeleteReduction } = useDeleteReduction(filterParams);

    const { mutate: handlePostReduction, isLoading: isPostReductionLoading } = usePostReduction({
        filterParams,
        onSuccess: () => {
            setShowEventReductionDialog(false);
            setSelectedRow(null);
        },
    });

    useEffect(() => {
        if (locationOptions?.length && selectedLocations.length === 0) {
            setSelectedLocations([locationOptions[0]]);
        }
    }, [locationOptions, selectedLocations, setSelectedLocations]);

    useEffect(() => {
        if (worktypeOptions?.length && selectedWorktypes.length === 0) {
            setSelectedWorktypes([worktypeOptions[0]]);
        }
    }, [worktypeOptions, selectedWorktypes, setSelectedWorktypes]);

    return (
        <Container>
            <GridContainer>
                <MultiSelectWorktypeLocDateRangeFilter
                    locationOptions={locationOptions}
                    selectedLocation={selectedLocation}
                    onSelectedLocationChange={setSelectedLocation}
                    selectedLocations={selectedLocations}
                    onSelectedLocationsChange={setSelectedLocations}
                    worktypeOptions={worktypeOptions}
                    selectedWorktypes={selectedWorktypes}
                    onSelectedWorktypesChange={setSelectedWorktypes}
                    startDate={startDate}
                    onStartDateChange={setStartDate}
                    endDate={endDate}
                    onEndDateChange={setEndDate}
                ></MultiSelectWorktypeLocDateRangeFilter>
                <Grid item>
                    <Button variant="contained" onClick={() => setShowEventReductionDialog(true)}>
                        <AddIcon />
                    </Button>
                    {showEventReductionDialog && (
                        <EventReductionDialog
                            dialogTitle={t('reduction.newReduction')}
                            showDialog={showEventReductionDialog}
                            onShowDialog={setShowEventReductionDialog}
                            record={
                                selectedRow
                                    ? {
                                          ...selectedRow,
                                          serieId: selectedRow?.reductionSerieId,
                                          serie: selectedRow?.serie,
                                      }
                                    : null
                            }
                            onRowSelect={setSelectedRow}
                            postRecord={handlePostReduction}
                            locationOptions={locationOptionsForDialog}
                            isLoading={isPostReductionLoading}
                        />
                    )}
                </Grid>
                {loggedInUser?.isTEFUser && (
                    <>
                        <Grid item>
                            <EventReductionExportButton
                                recordType="reduction"
                                rows={reductions}
                                start={startDate}
                                end={endDate}
                                pinnedBottomRowData={[
                                    {
                                        worktypeName: t('common.total'),
                                        duration: durationTotal,
                                        reduction: reductionTotal,
                                        id: '',
                                    },
                                ]}
                            />
                        </Grid>
                    </>
                )}
                <EventReductionTable
                    tableRows={reductions}
                    durationTotal={durationTotal}
                    reductionTotal={reductionTotal}
                    onRowEditConfirmation={() => setShowEventReductionDialog(true)}
                    onRowCopy={() => setShowEventReductionDialog(true)}
                    selectedRow={selectedRow}
                    onRowSelect={(row) => setSelectedRow(row)}
                    onRowDelete={handleDeleteReduction}
                    getRecordById={getReductionById}
                ></EventReductionTable>
            </GridContainer>
        </Container>
    );
};

export default Reduction;
